import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fuseConfig } from 'app/fuse-config';
import { FuseConfig } from '@fuse/types';
import { HttpClient } from '@angular/common/http';
import { Resolve } from '@angular/router';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface Locale {
    lang: string;
    data: Object;
}

@Injectable({
    providedIn: 'root'
})
export class FuseTranslationLoaderService implements OnDestroy, Resolve<any>
{
    public _fuseConfig: FuseConfig;
    private _unsubscribeAll: Subject<any>;
    public pageTranslationsLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public onLangChanged: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * Constructor
     *
     * @param {TranslateService} _translateService
     */
    constructor(
        private _translateService: TranslateService,
        public _http: HttpClient,
    ) {
        this._fuseConfig = fuseConfig;
        this._unsubscribeAll = new Subject();
    }

    /**
     * Resolver
     *
     * @returns {Observable<any> | Promise<any> | any}
     */
    resolve(): Observable<any> | Promise<any> | any {

        return new Promise<void>((resolve, reject) => {

            Promise.all([
                this.getLastLang(),
                this.getLanguages()
            ])
                .then(
                    () => {
                        resolve();
                    }
                )
                .catch(err => Promise.reject(err))
                .then(() => {
                }, err => {
                    // this._router.navigate(['/home']);
                });
        });
    }

    private processEmptyTranslations(object: any): any {
        const newObject: any = {};
        for (const key in object) {
            if (object.hasOwnProperty(key)) {
                if (typeof object[key] === 'object') {
                    newObject[key] = this.processEmptyTranslations(object[key]);
                } else if (typeof object[key] === 'string' && object[key] === '') {
                    // do not copy empty strings
                } else {
                    newObject[key] = object[key];
                }
            }
        }
        return newObject;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load translations
     *
     * @param {Locale} args
     */
    loadTranslations(...args: Locale[]): void {
        const locales = [...args];

        locales.forEach((locale) => {
            // use setTranslation() with the third argument set to true
            // to append translations instead of replacing them
            this._translateService.setTranslation(locale.lang, locale.data, true);
        });
    }

    /**
     * get translations
     *
     */
    getTranslations(languageCode: string, categoryId: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/translator/site/get-by-category/' + languageCode + '/' + categoryId)
                .subscribe(
                    response => {
                        this._translateService.setTranslation(languageCode, this.processEmptyTranslations(response['data']), true);
                        this.pageTranslationsLoaded.next(true);
                        resolve(true);
                        setTimeout(() => {
                            this._translateService.use(languageCode);
                        }, 1000);

                    },
                    reject
                );
        });
    }

    getMultipleTranslationCategories(languageCode: string, categoriesId: any): Promise<any> {

        const params = {
            categories: categoriesId
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/translator/site/get-by-categories/' + languageCode, params)
                .subscribe(
                    response => {
                        this.pageTranslationsLoaded.next(true);
                        resolve(true);
                        this._translateService.setTranslation(languageCode, this.processEmptyTranslations(response['data']), true);
                        setTimeout(() => {
                            this._translateService.use(languageCode);
                        }, 1000);

                    },
                    reject
                );
        });
    }

    getLastLang(): Promise<any> {

        const lang = localStorage.getItem('lang');
        const langName = localStorage.getItem('langName');

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/translator/site/get-default-language')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        if ((!lang || lang === 'undefined') && (!langName || langName === 'undefined')) {
                            resolve(response['data']);
                        }
                        else {
                            resolve({ code: lang, name: langName });
                        }
                    }, reject);

        });
    }


    setLastLang(lang): void {
        localStorage.setItem('lang', lang.id);
        localStorage.setItem('langName', lang.title);
        this.onLangChanged.next(true);
    }

    /**
     * get languages
     *
     */
    getLanguages(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/translator/site/get-languages')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response['data']);
                    }, reject);
        });
    }

    /**
     * Lifecycle hooks
     */

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
